import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LlmSettingsView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { StatusBadge } from '@/components/ui/status-badge';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';

const input = 'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm';

/**
 * Model providers, logical model profiles and budgets (ADR-017). Providers and profiles are
 * deployment configuration (read-only here); API keys are vault credentials referenced by name
 * (add them under Credentials). Budgets are per organization and editable by administrators.
 */
export function LlmSettingsPage() {
  const { can } = useSession();
  const manage = can('llm.manage');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['llm-settings'],
    queryFn: () => api<LlmSettingsView>('/api/v1/llm/settings'),
  });
  const d = q.data;
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Models & budgets"
        description="Which provider serves each logical model profile, which data classifications each provider may receive, and spending limits."
      />
      {q.isError && <ErrorNote error={q.error} />}
      {d && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Providers</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <thead>
                  <tr>
                    <Th>Provider</Th>
                    <Th>Kind</Th>
                    <Th>Location</Th>
                    <Th>May receive</Th>
                    <Th>API key (vault)</Th>
                  </tr>
                </thead>
                <tbody>
                  {d.providers.map((p) => (
                    <tr key={p.name}>
                      <Td>{p.name}</Td>
                      <Td>
                        <Mono>{p.kind}</Mono>
                        {!p.available && <p className="text-xs text-fail">adapter not installed</p>}
                      </Td>
                      <Td>{p.external ? 'External service' : 'Platform infrastructure'}</Td>
                      <Td>
                        {p.eligibleClassifications.join(', ') || 'nothing (deny until configured)'}
                      </Td>
                      <Td>
                        {p.credential ? (
                          <StatusBadge
                            status={p.credentialConfigured ? 'ok' : 'fail'}
                            label={
                              p.credentialConfigured
                                ? `${p.credential} configured`
                                : `${p.credential} missing`
                            }
                          />
                        ) : (
                          '—'
                        )}
                      </Td>
                    </tr>
                  ))}
                  {d.providers.length === 0 && (
                    <tr>
                      <Td colSpan={5} className="text-muted">
                        No providers configured (LLM_CONFIG).
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <p className="px-4 py-3 text-xs text-muted">
                RESTRICTED data is never sent to an external provider. Missing keys: add a
                credential with the name shown under Credentials.
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Model profiles</CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              <Table>
                <thead>
                  <tr>
                    <Th>Profile</Th>
                    <Th>Provider</Th>
                    <Th>Model</Th>
                    <Th className="text-right">Max output tokens</Th>
                  </tr>
                </thead>
                <tbody>
                  {d.profiles.map((p) => (
                    <tr key={p.profile}>
                      <Td>
                        <Mono>{p.profile}</Mono>
                      </Td>
                      <Td>{p.provider}</Td>
                      <Td>
                        <Mono>{p.model}</Mono>
                      </Td>
                      <Td className="text-right tabular-nums">{p.maxOutputTokens}</Td>
                    </tr>
                  ))}
                  {d.profiles.length === 0 && (
                    <tr>
                      <Td colSpan={4} className="text-muted">
                        No profiles configured.
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
          <BudgetsCard
            key={JSON.stringify(d.budgets)}
            settings={d}
            manage={manage}
            onSaved={() => void qc.invalidateQueries({ queryKey: ['llm-settings'] })}
          />
        </>
      )}
    </div>
  );
}

/** Budget form; initialised from props and re-mounted (key) when the saved values change. */
function BudgetsCard({
  settings,
  manage,
  onSaved,
}: {
  settings: LlmSettingsView;
  manage: boolean;
  onSaved: () => void;
}) {
  const s = (v: number | null) => (v === null ? '' : String(v));
  const [b, setB] = useState({
    perExecutionLimitUsd: s(settings.budgets.perExecutionLimitUsd),
    dailyLimitUsd: s(settings.budgets.dailyLimitUsd),
    monthlyLimitUsd: s(settings.budgets.monthlyLimitUsd),
  });
  const save = useMutation({
    mutationFn: () => {
      const n = (v: string) => (v.trim() === '' ? null : Number(v));
      return api<LlmSettingsView>('/api/v1/llm/budgets', {
        method: 'PUT',
        body: {
          perExecutionLimitUsd: n(b.perExecutionLimitUsd),
          dailyLimitUsd: n(b.dailyLimitUsd),
          monthlyLimitUsd: n(b.monthlyLimitUsd),
        },
      });
    },
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Budgets</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="mb-3 text-sm">
          Spent today <span className="tabular-nums">USD {settings.spend.todayUsd.toFixed(4)}</span>{' '}
          · this month{' '}
          <span className="tabular-nums">USD {settings.spend.monthUsd.toFixed(4)}</span>
          <span className="ml-2 text-xs text-muted">
            (
            {settings.budgets.source === 'organization'
              ? 'organization limits'
              : 'deployment defaults'}
            )
          </span>
        </p>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-4">
          {(
            [
              ['perExecutionLimitUsd', 'Per execution (USD)'],
              ['dailyLimitUsd', 'Daily (USD)'],
              ['monthlyLimitUsd', 'Monthly (USD)'],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="text-xs text-muted">
              {label}
              <input
                className={input}
                type="number"
                min="0"
                step="0.01"
                placeholder="no limit"
                disabled={!manage}
                value={b[k]}
                onChange={(e) => setB({ ...b, [k]: e.target.value })}
              />
            </label>
          ))}
          {manage && (
            <div className="flex items-end">
              <Button type="submit" variant="primary" disabled={save.isPending}>
                Save budgets
              </Button>
            </div>
          )}
        </form>
        {save.isError && (
          <div className="mt-3">
            <ErrorNote error={save.error} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
